import {
  FC,
  ImgHTMLAttributes,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { GET_BASE_URL_IMAGE } from '../constants/apiEndpoints';

type Resort = 'disneyland' | 'disneyworld';

type ChatImageWithFallbackProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'src'
> & {
  filename: unknown;
  resort?: Resort;
};

const fallbackQualityLabels = ['original', 'medium', 'thumbnail'];

export function isChatImageFilename(filename: unknown): filename is string {
  return typeof filename === 'string' && filename.trim().includes('c_img');
}

export function buildChatImageFallbackUrls(
  filename: unknown,
  resort: Resort = 'disneyland'
): string[] {
  if (!isChatImageFilename(filename)) {
    return [];
  }

  const safeFilename = filename.trim();
  return [
    `${GET_BASE_URL_IMAGE}/${resort}/chat_images/${safeFilename}`,
    `${GET_BASE_URL_IMAGE}/${resort}/chat_images_medium/${safeFilename}`,
    `${GET_BASE_URL_IMAGE}/${resort}/chat_images_thumbnail/${safeFilename}`,
  ];
}

export const ChatImageWithFallback: FC<ChatImageWithFallbackProps> = ({
  filename,
  resort = 'disneyland',
  alt = '',
  onError,
  ...imgProps
}) => {
  const urls = useMemo(
    () => buildChatImageFallbackUrls(filename, resort),
    [filename, resort]
  );
  const [urlIndex, setUrlIndex] = useState(0);

  useEffect(() => {
    setUrlIndex(0);
  }, [urls]);

  if (urls.length === 0 || urlIndex >= urls.length) {
    return null;
  }

  const handleError = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    onError?.(event);
    setUrlIndex((currentIndex) => currentIndex + 1);
  };

  return (
    <img
      {...imgProps}
      src={urls[urlIndex]}
      alt={alt}
      data-mw-chat-image-quality={fallbackQualityLabels[urlIndex] || 'fallback'}
      onError={handleError}
    />
  );
};
